'use client'

import { useEffect, useState, useRef } from 'react'
import { getLinkLiveSession, LiveSessionState, LinkLiveSession } from '@/lib/voice/link-live-session'
import { Mic, X, Loader2, Volume2, MessageSquare } from 'lucide-react'

export function LinkLiveOverlay() {
  const [state, setState] = useState<LiveSessionState>(LiveSessionState.IDLE)
  const [isVisible, setIsVisible] = useState(false)
  const [transcripts, setTranscripts] = useState<{ text: string, isUser: boolean }[]>([])
  const sessionRef = useRef<LinkLiveSession | null>(null)
  const transcriptBoxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const session = getLinkLiveSession()
    sessionRef.current = session

    const unsubState = session.subscribe((newState) => {
      setState(newState)
      if (newState !== LiveSessionState.IDLE) {
        setIsVisible(true)
      }
    })

    const unsubTranscript = session.onTranscript((text, isUser) => {
        setTranscripts(prev => [...prev, { text, isUser }])
        // Scroll to bottom
        if (transcriptBoxRef.current) {
            setTimeout(() => {
                transcriptBoxRef.current?.scrollTo({ top: transcriptBoxRef.current.scrollHeight, behavior: 'smooth' })
            }, 100)
        }
    })

    return () => {
      unsubState()
      unsubTranscript()
    }
  }, [])

  const handleClose = () => {
    sessionRef.current?.stopSession()
    setIsVisible(false)
    setTranscripts([])
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity duration-300">
      <div className="w-full max-w-md bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-800 flex flex-col h-[600px]">
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-semibold text-white">Link Live</span>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Visualizer Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 relative">
          {/* Ambient Glow */}
          <div className={`absolute inset-0 bg-blue-500/10 blur-3xl transition-opacity duration-1000 ${state === LiveSessionState.SPEAKING ? 'opacity-100' : 'opacity-30'}`} />
          
          {/* Main Visual */}
          <div className="relative z-10">
            {state === LiveSessionState.LISTENING && (
              <div className="w-32 h-32 rounded-full bg-blue-600/20 flex items-center justify-center animate-pulse">
                <Mic className="w-12 h-12 text-blue-400" />
              </div>
            )}
            
            {state === LiveSessionState.THINKING && (
              <div className="w-32 h-32 rounded-full bg-purple-600/20 flex items-center justify-center relative">
                <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full animate-spin border-t-purple-500" />
                <Loader2 className="w-12 h-12 text-purple-400 animate-spin" />
              </div>
            )}
            
            {state === LiveSessionState.SPEAKING && (
              <div className="flex items-center justify-center gap-1 h-32">
                {[...Array(5)].map((_, i) => (
                  <div 
                    key={i} 
                    className="w-3 bg-blue-400 rounded-full animate-bounce" 
                    style={{ 
                        height: '40px',
                        animationDuration: `${0.5 + i * 0.1}s`,
                        animationDelay: `${i * 0.05}s` 
                    }} 
                  />
                ))}
              </div>
            )}
            
            {state === LiveSessionState.IDLE && (
               <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center">
                 <Mic className="w-12 h-12 text-gray-600" />
               </div>
            )}
          </div>

          <p className="mt-8 text-lg font-medium text-gray-300 text-center">
            {state === LiveSessionState.LISTENING && "Listening..."}
            {state === LiveSessionState.THINKING && "Thinking..."}
            {state === LiveSessionState.SPEAKING && "Speaking..."}
            {state === LiveSessionState.IDLE && "Ready"}
          </p>
        </div>

        {/* Transcript Area */}
        <div ref={transcriptBoxRef} className="h-48 overflow-y-auto p-4 bg-black/20 space-y-3 border-t border-gray-800">
            {transcripts.length === 0 && (
                <p className="text-gray-500 text-center text-sm italic">Conversation started...</p>
            )}
            {transcripts.map((t, i) => (
                <div key={i} className={`flex ${t.isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
                        t.isUser 
                        ? 'bg-blue-600 text-white rounded-br-none' 
                        : 'bg-gray-800 text-gray-200 rounded-bl-none'
                    }`}>
                        {t.text}
                    </div>
                </div>
            ))}
        </div>
      </div>
    </div>
  )
}
